import cn from 'classnames'
import Image from 'next/image'
import {FC, ReactNode} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Link, useTranslation} from '@/i18n'
import {HEADER_MAX_WIDTH, HEADER_PADDING_DESKTOP} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Text, TextFontWeight, TextVariant} from '../content/Text'

const LOGO_MAX_WIDTH = 130
const LOGO_MAX_HEIGHT = 44

// Fit the logo into the max box without upscaling. next/image then serves 1x/2x versions of
// exactly this size, instead of the browser guessing the size from a srcset. The content sizes
// are rounded, so the image is drawn with object-fit: contain to keep the logo's real proportions.
const getLogoSize = (width: number, height: number) => {
  const scale = Math.min(1, LOGO_MAX_WIDTH / width, LOGO_MAX_HEIGHT / height)
  return {width: Math.round(width * scale), height: Math.round(height * scale)}
}

type InfoBannerProps = {
  imageSrc?: string
  imageAlt?: string
  imageWidth?: number
  imageHeight?: number
  icon?: ReactNode
  title?: string
  description: string | ReactNode
  button?: ReactNode
  linkHref?: string
  linkText?: string
  trackedEvent?: string
  fullWidth?: boolean
  textColor?: string
  backgroundColor: string
  onClose?: () => void
  isCentered?: boolean
}

export const InfoBanner: FC<React.PropsWithChildren<InfoBannerProps>> = ({
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  icon,
  title,
  description,
  button,
  linkHref,
  linkText,
  trackedEvent,
  fullWidth = false,
  textColor = theme.palette.text.default,
  backgroundColor,
  onClose,
  isCentered = false,
}) => {
  const {t} = useTranslation()
  return (
    <>
      <div className={cn('wrapper', fullWidth && 'full-width')}>
        <div className={cn('banner', icon && 'has-icon')}>
          <div className="content">
            {imageSrc && imageAlt && imageWidth && imageHeight && (
              <div className="logo">
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  {...getLogoSize(imageWidth, imageHeight)}
                  style={{display: 'block', objectFit: 'contain', objectPosition: 'left'}}
                />
              </div>
            )}
            {icon && <div className="icon">{icon}</div>}
            <div>
              {title && (
                <div className="title">
                  <Text
                    variant={TextVariant.text14}
                    fontWeight={TextFontWeight.bold}
                    color={textColor}
                  >
                    {title}
                  </Text>
                </div>
              )}
              <Text variant={TextVariant.text14} color={textColor}>
                {description}
              </Text>
            </div>
          </div>
          <div className="actions">
            {onClose && (
              <div className="button-wrapper mobile">
                <Button
                  type="button"
                  size={ButtonSize.small}
                  variant={ButtonVariant.tertiary}
                  onClick={() => onClose()}
                >
                  {t('close-btn-text')}
                </Button>
              </div>
            )}
            {button && <div className="button-wrapper">{button}</div>}
            {linkHref && linkText && trackedEvent && (
              <div className="button-wrapper">
                <Link href={linkHref} onClick={() => analytics.track(trackedEvent)}>
                  <Button variant={ButtonVariant.tertiary} type="button" size={ButtonSize.small}>
                    {linkText}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .full-width {
          margin: 0 ${HEADER_PADDING_DESKTOP};
        }

        .banner {
          padding: ${theme.spacing[3]};
          background-color: ${backgroundColor};
          border-radius: 8px;
        }

        .full-width .banner {
          display: flex;
          align-items: center;
          justify-content: ${isCentered ? 'center' : 'space-between'};
          margin: 0 auto;
          max-width: ${HEADER_MAX_WIDTH};
          padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
        }

        .full-width .content {
          display: flex;
        }

        .has-icon .content {
          display: flex;
          align-items: flex-start;
        }

        .logo {
          flex-shrink: 0;
          margin-bottom: ${theme.spacing[2]};
        }

        .full-width .logo {
          align-self: center;
          margin-right: ${theme.spacing[3]};
          margin-bottom: 0;
        }

        .icon {
          margin-right: calc(${theme.spacing[3]} - ${theme.spacing[1]});
        }

        .title {
          margin-bottom: ${theme.spacing[1]};
        }

        .actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        .button-wrapper {
          margin-top: ${theme.spacing[3]};
          text-align: right;
          white-space: nowrap;
        }

        .button-wrapper + .button-wrapper {
          margin-left: ${theme.spacing[3]};
        }

        .full-width .button-wrapper {
          margin-top: 0;
          margin-left: ${theme.spacing[4]};
        }

        .mobile {
          display: none;
        }

        @media ${TABLET_SCREENSIZE} {
          .full-width {
            margin: 0;
          }

          .full-width .banner {
            border-radius: 0;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .mobile {
            display: block;
          }

          .full-width .banner {
            display: block;
          }

          .full-width .button-wrapper {
            margin-top: ${theme.spacing[3]};
          }

          .full-width .logo {
            align-self: flex-start;
          }
        }
      `}</style>
    </>
  )
}
